import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import BDPLayout from "../../components/bdp/BDPLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { DollarSign, TrendingUp, Award, Loader2 } from "lucide-react";

const BDPCommissions = () => {
  const { API, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/bdp/commissions`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setData(await r.json());
      } catch { // error handled silently
        console.error("Request failed:", error);
    }
      setLoading(false);
    })();
  }, [API, token]);

  if (loading) return <BDPLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div></BDPLayout>;

  const summary = data?.summary || {};
  const items = data?.items || [];

  return (
    <BDPLayout>
      <div className="space-y-6" data-testid="bdp-commissions">
        <h1 className="text-2xl font-bold text-[#1A2636]">Commission Details</h1>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: "Total Commission", value: summary.total_commission, icon: DollarSign, gradient: "from-emerald-500 to-teal-600" },
            { label: "Deal Commission", value: summary.deal_commission, icon: TrendingUp, gradient: "from-blue-500 to-indigo-600" },
            { label: "Ad-hoc / Bonuses", value: summary.adhoc_commission, icon: Award, gradient: "from-purple-500 to-violet-600" },
          ].map((s, i) => (
            <Card key={s.key || s.name || s.title || i} className="border-0 shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-4">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">{s.label}</p>
                    <p className="text-xl font-bold text-[#1A2636]">${(s.value || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
                <div className={`h-1 bg-gradient-to-r ${s.gradient}`} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Commission log */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h2 className="font-semibold text-[#1A2636] mb-4">Commission History ({items.length} items)</h2>
            {items.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b">
                      <th className="pb-3 pr-4">Date</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Client</th>
                      <th className="pb-3 pr-4">Description</th>
                      <th className="pb-3 pr-4 text-right">Sale Amount</th>
                      <th className="pb-3 pr-4 text-right">Rate</th>
                      <th className="pb-3 text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={item.title || item.name || item.label || i} className="border-b border-gray-50 last:border-0">
                        <td className="py-3 pr-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                        <td className="py-3 pr-4">
                          <Badge className={item.type === "deal" ? "bg-emerald-50 text-emerald-700 text-[10px]" : "bg-purple-50 text-purple-700 text-[10px]"}>
                            {item.type === "deal" ? "Deal" : "Bonus"}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          {item.client_name ? (
                            <div>
                              <p className="font-medium text-[#1A2636]">{item.client_name}</p>
                              <p className="text-[11px] text-gray-400">{item.client_email}</p>
                            </div>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="py-3 pr-4 font-medium text-[#1A2636]">{item.description}</td>
                        <td className="py-3 pr-4 text-right text-gray-600">{item.sale_amount ? `$${Number(item.sale_amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "-"}</td>
                        <td className="py-3 pr-4 text-right text-gray-500">{item.rate ? `${(item.rate * 100).toFixed(0)}%` : "-"}</td>
                        <td className="py-3 text-right font-bold text-emerald-600">${Number(item.commission).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-gray-400 text-sm">No commissions earned yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </BDPLayout>
  );
};

export default BDPCommissions;
